import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { isFeatureEnabled } from 'common/utils';
import { SocketDispatcherMiddlewareType } from 'modules/webSocket/state';

import {
  audienceJoined_WS,
  broadcastGuestPromoted_WS,
  broadcastStarted_WS,
  guestDisconnected_WS,
  guestExitedMeeting_WS,
  guestJoinedRoom_WS,
  joinRoom_WS,
  meetingRoomClosed_WS,
} from './actions';
import { setNatteringResumeCountdown, setRematchCountdown } from './eventSlice.actions';
import { EventPhase, NatteringPhase, NatteringStatus } from './types';

// Feature flags are read once at module load — intentional and safe because flags resolve from
// build-time env config (window._env_), not a runtime/remote source, so they can't change during a
// session. If that ever changes these (and the other module-level `isFeatureEnabled` reads) would
// need to move inside the middleware to see live updates.
const isOverlayEnabled = isFeatureEnabled('NatteringDisconnectOverlay');
const isJitPermissionsEnabled = isFeatureEnabled('JustInTimePermissions');

const guestExitedDelayedEffects = new Map<string, NodeJS.Timeout>();
const guestDisconnectedDelayedEffects = new Map<string, NodeJS.Timeout>();

export const GUEST_EXITED_DELAY = 2000;
export const GUEST_DISCONNECTED_DELAY = 2000;
export const REMATCH_ROOM_SWITCH_DELAY = 3000;
/** Length of the "found someone → connecting 3,2,1" beat on a FIRST match (mirrors the rematch). */
export const FIRST_MATCH_CONNECTING_DELAY = 3000;
/** Length of the "broadcast has resumed → returning 3,2,1" beat before the broadcast mounts. */
export const BROADCAST_RESUME_DELAY = 3000;

type PendingRematchRoomSwitch = {
  closePath: string;
  closePayload: ReturnType<typeof meetingRoomClosed_WS>['payload'];
  joinPath?: string;
  joinPayload?: ReturnType<typeof joinRoom_WS>['payload'];
  timeout: NodeJS.Timeout;
};

let pendingRematchRoomSwitch: PendingRematchRoomSwitch | null = null;

const dispatchPendingRematchRoomSwitch = (dispatch: Dispatch<UnknownAction>) => {
  if (!pendingRematchRoomSwitch) return;

  const pending = pendingRematchRoomSwitch;
  pendingRematchRoomSwitch = null;

  dispatch(setRematchCountdown(null));

  dispatch({
    type: pending.closePath,
    payload: pending.closePayload,
  });

  if (pending.joinPath) {
    dispatch({
      type: pending.joinPath,
      payload: pending.joinPayload,
    });
  }
};

const clearPendingRematchRoomSwitch = () => {
  if (!pendingRematchRoomSwitch) return;
  clearTimeout(pendingRematchRoomSwitch.timeout);
  pendingRematchRoomSwitch = null;
};

type PendingFirstMatchJoin = {
  joinPayload: ReturnType<typeof joinRoom_WS>['payload'];
  timeout: NodeJS.Timeout;
};

// A FIRST match (the participant was waiting in the matching pool, not yet in any room) arrives as
// a lone joinRoom_WS. We hold it for the connecting countdown so the matching surface can play the
// "found → 3,2,1" beat; only when it's released does the room mount and Agora connect.
let pendingFirstMatchJoin: PendingFirstMatchJoin | null = null;

const dispatchPendingFirstMatchJoin = (dispatch: Dispatch<UnknownAction>) => {
  if (!pendingFirstMatchJoin) return;
  const pending = pendingFirstMatchJoin;
  pendingFirstMatchJoin = null;
  // The joinRoom_WS reducer clears rematchCountdown itself, so releasing the buffered join both
  // reveals the room and tears the countdown down in one step.
  dispatch({ type: joinRoom_WS.type, payload: pending.joinPayload });
};

const clearPendingFirstMatchJoin = () => {
  if (!pendingFirstMatchJoin) return;
  clearTimeout(pendingFirstMatchJoin.timeout);
  pendingFirstMatchJoin = null;
};

type PendingBroadcastResume = {
  payload: ReturnType<typeof broadcastStarted_WS>['payload'];
  timeout: NodeJS.Timeout;
};

// The round ended without this participant matching, so they're still in the matching teleprompter
// when the broadcast resumes (a lone broadcastStarted_WS). We hold it for the resume countdown so the
// teleprompter can play "Broadcast has resumed → 3,2,1"; releasing it mounts the broadcast.
let pendingBroadcastResume: PendingBroadcastResume | null = null;

const dispatchPendingBroadcastResume = (dispatch: Dispatch<UnknownAction>) => {
  if (!pendingBroadcastResume) return;
  const pending = pendingBroadcastResume;
  pendingBroadcastResume = null;
  // The broadcastStarted_WS reducer clears natteringResumeCountdown itself, so releasing the
  // buffered message both mounts the broadcast and tears the countdown down in one step.
  dispatch({ type: broadcastStarted_WS.type, payload: pending.payload });
};

const clearPendingBroadcastResume = () => {
  if (!pendingBroadcastResume) return;
  clearTimeout(pendingBroadcastResume.timeout);
  pendingBroadcastResume = null;
};

/** @internal — exposed only so tests can isolate the module-level pending-rematch slot. */
export const __resetEventSocketDispatcherMiddlewareForTesting = () => {
  clearPendingRematchRoomSwitch();
  clearPendingFirstMatchJoin();
  clearPendingBroadcastResume();
  guestExitedDelayedEffects.forEach(clearTimeout);
  guestExitedDelayedEffects.clear();
  guestDisconnectedDelayedEffects.forEach(clearTimeout);
  guestDisconnectedDelayedEffects.clear();
};

export const eventSocketDispatcherMiddleware: SocketDispatcherMiddlewareType = (
  { dispatch, getState },
  { path, payload }
) => {
  if (path === meetingRoomClosed_WS.type) {
    const { id } = payload as ReturnType<typeof meetingRoomClosed_WS>['payload'];
    const { activeRoom } = getState().event;
    const shouldDelayRoomSwitch = activeRoom?.id === id && !activeRoom.joinedAsSupport;

    if (!shouldDelayRoomSwitch) {
      return false;
    }

    if (!isOverlayEnabled) {
      return false;
    }

    if (pendingRematchRoomSwitch?.closePayload.id === id) {
      // Duplicate close for the same room — refresh the timer and keep the modal's countdown
      // anchored to the new endsAt so it doesn't show a stale "1…" while time is being added back.
      clearTimeout(pendingRematchRoomSwitch.timeout);
      pendingRematchRoomSwitch.timeout = setTimeout(() => {
        dispatchPendingRematchRoomSwitch(dispatch);
      }, REMATCH_ROOM_SWITCH_DELAY);
      dispatch(setRematchCountdown({ endsAt: Date.now() + REMATCH_ROOM_SWITCH_DELAY }));
      return true;
    }

    clearPendingRematchRoomSwitch();
    pendingRematchRoomSwitch = {
      closePath: path,
      closePayload: { id },
      timeout: setTimeout(() => {
        dispatchPendingRematchRoomSwitch(dispatch);
      }, REMATCH_ROOM_SWITCH_DELAY),
    };
    dispatch(setRematchCountdown({ endsAt: Date.now() + REMATCH_ROOM_SWITCH_DELAY }));
    return true;
  }

  if (path === joinRoom_WS.type && pendingRematchRoomSwitch) {
    const { id } = payload as ReturnType<typeof joinRoom_WS>['payload'];
    if (id !== pendingRematchRoomSwitch.closePayload.id) {
      pendingRematchRoomSwitch.joinPath = path;
      pendingRematchRoomSwitch.joinPayload = payload as ReturnType<typeof joinRoom_WS>['payload'];
      return true;
    }
    // If id === closePayload.id the backend re-sent a join for the room we're already leaving.
    // Fall through and dispatch immediately — the buffered close will still fire and clean up.
  }

  // First match (just-in-time permissions): the participant is waiting in the matching pool and
  // not yet in a room. Buffer the join so the matching surface can play the connecting countdown,
  // then release it (mounting the room + connecting Agora) once the countdown elapses. A
  // reload/rejoin into an existing room isn't "matching", so it loads straight in.
  if (isJitPermissionsEnabled && path === joinRoom_WS.type && !pendingRematchRoomSwitch) {
    const { event } = getState();
    const isFirstMatch =
      event.natteringPhase === NatteringPhase.WaitingToStart &&
      event.natteringStatus === NatteringStatus.Accepted &&
      !event.activeRoom;

    if (isFirstMatch) {
      const joinPayload = payload as ReturnType<typeof joinRoom_WS>['payload'];
      if (pendingFirstMatchJoin) {
        // A newer join supersedes the buffered one; keep the running countdown.
        pendingFirstMatchJoin.joinPayload = joinPayload;
        return true;
      }
      pendingFirstMatchJoin = {
        joinPayload,
        timeout: setTimeout(() => dispatchPendingFirstMatchJoin(dispatch), FIRST_MATCH_CONNECTING_DELAY),
      };
      dispatch(setRematchCountdown({ endsAt: Date.now() + FIRST_MATCH_CONNECTING_DELAY }));
      return true;
    }
  }

  // Broadcast resumed (just-in-time permissions): the round ended without this participant being
  // matched, so they're still in the opening matching teleprompter when the broadcast comes back.
  // Buffer the broadcastStarted_WS so the teleprompter can play the "broadcast has resumed → 3,2,1"
  // beat, then release it (mounting the broadcast) once the countdown elapses.
  //
  // Gate tightly on "the teleprompter is actually mounted": that's a LIVE nattering round
  // (eventPhase === Nattering) still WaitingToStart with no room — exactly when NatteringRoomRoot
  // shows the round experience. Without the eventPhase check this also fires for someone joining an
  // event that's already broadcasting (their phases sit at the WaitingToStart/no-room defaults), so
  // every broadcast joiner would eat the delay. People in rooms (activeRoom) or joining straight
  // into broadcast must go in with no delay.
  if (isJitPermissionsEnabled && path === broadcastStarted_WS.type) {
    const { event } = getState();
    const isInMatchingTeleprompter =
      event.eventPhase === EventPhase.Nattering &&
      event.natteringPhase === NatteringPhase.WaitingToStart &&
      !event.activeRoom;

    if (isInMatchingTeleprompter) {
      const resumePayload = payload as ReturnType<typeof broadcastStarted_WS>['payload'];
      if (pendingBroadcastResume) {
        // A newer broadcastStarted supersedes the buffered one; keep the running countdown.
        pendingBroadcastResume.payload = resumePayload;
        return true;
      }
      pendingBroadcastResume = {
        payload: resumePayload,
        timeout: setTimeout(() => dispatchPendingBroadcastResume(dispatch), BROADCAST_RESUME_DELAY),
      };
      dispatch(setNatteringResumeCountdown({ endsAt: Date.now() + BROADCAST_RESUME_DELAY }));
      return true;
    }
  }

  // Delay guestExitedMeeting_WS to prevent flickering when users briefly disconnect
  if (path === guestExitedMeeting_WS.type) {
    const { id } = payload as ReturnType<typeof guestExitedMeeting_WS>['payload'];
    const key = `${guestExitedMeeting_WS.type}${id}`;

    const existingDispatch = guestExitedDelayedEffects.get(key);
    if (existingDispatch) {
      clearTimeout(existingDispatch);
    }

    const delayedDispatch = setTimeout(() => {
      dispatch({
        type: path,
        payload,
      });
      guestExitedDelayedEffects.delete(key);
    }, GUEST_EXITED_DELAY);
    guestExitedDelayedEffects.set(key, delayedDispatch);
    return true;
  }

  // Cancel pending guestExitedMeeting if user is promoted or rejoins
  if (path === broadcastGuestPromoted_WS.type || path === audienceJoined_WS.type) {
    const id =
      path === broadcastGuestPromoted_WS.type
        ? (payload as ReturnType<typeof broadcastGuestPromoted_WS>['payload']).accountId
        : (payload as ReturnType<typeof audienceJoined_WS>['payload']).id;
    const key = `${guestExitedMeeting_WS.type}${id}`;
    const delayedDispatch = guestExitedDelayedEffects.get(key);
    if (delayedDispatch) {
      clearTimeout(delayedDispatch);
      guestExitedDelayedEffects.delete(key);
    }
  }

  // Delay guestDisconnected_WS to prevent flickering when users briefly disconnect
  if (path === guestDisconnected_WS.type) {
    const { roomId, userId } = payload as ReturnType<typeof guestDisconnected_WS>['payload'];
    const key = `${guestDisconnected_WS.type}${roomId}${userId}`;

    const existingDispatch = guestDisconnectedDelayedEffects.get(key);
    if (existingDispatch) {
      clearTimeout(existingDispatch);
    }

    const delayedDispatch = setTimeout(() => {
      dispatch({
        type: path,
        payload,
      });
      guestDisconnectedDelayedEffects.delete(key);
    }, GUEST_DISCONNECTED_DELAY);
    guestDisconnectedDelayedEffects.set(key, delayedDispatch);
    return true;
  }

  // Cancel pending guestDisconnected and guestExitedMeeting if user rejoins room
  if (path === guestJoinedRoom_WS.type) {
    const { roomId, userId } = payload as ReturnType<typeof guestJoinedRoom_WS>['payload'];

    // Cancel pending guestDisconnected
    const disconnectedKey = `${guestDisconnected_WS.type}${roomId}${userId}`;
    const delayedDisconnected = guestDisconnectedDelayedEffects.get(disconnectedKey);
    if (delayedDisconnected) {
      clearTimeout(delayedDisconnected);
      guestDisconnectedDelayedEffects.delete(disconnectedKey);
    }

    // Cancel pending guestExitedMeeting
    const exitedKey = `${guestExitedMeeting_WS.type}${userId}`;
    const delayedExited = guestExitedDelayedEffects.get(exitedKey);
    if (delayedExited) {
      clearTimeout(delayedExited);
      guestExitedDelayedEffects.delete(exitedKey);
    }
  }

  return false;
};
